import React, { useState, useRef } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate, useLocation } from "react-router-dom";
import { createBanner } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { CTooltip } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilInfo } from "@coreui/icons";

const CreateBanners = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const location = useLocation();
  const { totalBanners, usedSequences = [] } = location.state || {};

  const initialValues = {
    title: "",
    sequence: null,
    image: "",
    type: "",
    url_link: "",
  };

  const sequenceArray = Array.from({ length: 7 }, (_, i) => i + 1);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    sequence: Yup.number()
      .required("Sequence is required")
      .integer("Sequence must be a whole number")
      .positive("Sequence must be positive")
      .max(7, "Sequence must be 7 or less")
      .typeError("Sequence must be a number"),
    image: Yup.mixed()
      .required("Image is required")
      .test(
        "imageFormat",
        "Unsupported file format. Only image allowed.",
        (value) => {
          if (!value) return true;

          if (value instanceof File) {
            return [
              "image/jpeg",
              "image/png",
              "image/jpg",
              "image/gif",
              "image/webp",
              "image/svg+xml",
            ].includes(value.type);
          }

          return true;
        }
      ),
    type: Yup.string()
      .required("Type is required")
      .oneOf(
        [
          "auto",
          "travel",
          "group",
          "property",
          "foreign_workers",
          "personal_accidents",
          "offshore",
          "trust_will",
          "pet",
          "link",
          "term_life",
          "health",
        ],
        "Invalid type"
      ),
    url_link: Yup.string().when("type", {
      is: (val) => val === "link",
      then: (schema) => schema.url("Invalid URL format").required("URL Link is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("sequence", values.sequence);
      formData.append("status", values.status);
      formData.append("type", values.type);
      formData.append("url_link", values.url_link);

      const image = values.image;
      if (image) {
        formData.append("image", image);
      } else {
        console.error("No image selected.");
        toast.error("No image selected.");
        return;
      }

      try {
        await createBanner(formData);
        toast.success("Banner created successfully!");
        navigate("/banners");
      } catch (error) {
        console.error("Failed to create banner:", error);
        toast.error(
          error.message || "Failed to create banner. Please try again."
        );
        setErrors({ submit: "Failed to create banner", ...error.fieldErrors });
        Object.keys(values).forEach((field) =>
          setFieldTouched(field, true, false)
        );
      } finally {
        setSubmitting(false);
        setIsSubmittingForm(false);
      }
    } catch (error) {
      console.error("Failed to create banner:", error);
      toast.error(
        error.message || "Failed to create banner. Please try again."
      );
      setErrors({ submit: "Failed to create banner", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, isSubmitting, errors, touched, values }) => (
          <Form>
            {isSubmittingForm && (
              <div className="form-overlay">
                <CSpinner color="primary" />
              </div>
            )}
            <div className="card border-light">
              <div className="card-body">
                {errors.submit && (
                  <div className="alert alert-danger">{errors.submit}</div>
                )}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="title" className="form-label">
                      <span className="text-danger">*</span> Title
                    </label>{" "}
                    <Field
                      name="title"
                      as="input"
                      type="text"
                      placeholder="Enter Title"
                      className={`form-control ${
                        touched.title && errors.title ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label htmlFor="sequence" className="form-label">
                      <span className="text-danger">*</span> Sequence
                    </label>{" "}
                    <div className="input-group">
                      <Field
                        name="sequence"
                        as="select"
                        placeholder="Select the Sequence"
                        className={`form-select ${
                          touched.sequence && errors.sequence
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        {sequenceArray
                          .filter(
                            (num, index, self) => self.indexOf(num) === index
                          )
                          .map((num) => {
                            const status =
                              usedSequences.includes(num)
                                ? "[Taken]"
                                : "";

                            return (
                              <option key={num} value={num}>
                                {num} {status}
                              </option>
                            );
                          })}
                      </Field>
                    </div>
                    <ErrorMessage
                      name="sequence"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="type" className="form-label">
                      <span className="text-danger">*</span> Type
                    </label>{" "}
                    <Field
                      name="type"
                      as="select"
                      className={`form-select ${
                        touched.type && errors.type ? "is-invalid" : ""
                      }`}
                    >
                      <option value="" label="Select type" />
                      <option value="auto" label="Auto" />
                      <option value="term_life" label="Term Life" />
                      <option value="health" label="Health" />
                      <option value="travel" label="Travel" />
                      <option value="group" label="Group" />
                      <option value="property" label="Property" />
                      <option value="foreign_workers" label="Foreign Workers" />
                      <option value="personal_accidents" label="Personal Accidents" />
                      <option value="offshore" label="Offshore" />
                      <option value="trust_will" label="Trust & Will" />
                      <option value="pet" label="Pet" />
                      <option value="link" label="Link" />
                    </Field>
                    <ErrorMessage
                      name="type"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  {values.type === "link" && (
                    <div className="col-md-6 mb-3">
                      <label htmlFor="url_link" className="form-label">
                        <span className="text-danger">*</span> URL Link
                      </label>{" "}
                      <Field
                        name="url_link"
                        as="input"
                        type="text"
                        placeholder="Enter URL Link"
                        className={`form-control ${
                          touched.url_link && errors.url_link ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="url_link"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="image" className="form-label">
                      <span className="text-danger">*</span> Upload Image
                    </label>
                    <CTooltip
                      content={
                        <>
                          <ul style={{ textAlign: "left" }}>
                            <li>
                              Supported formats: PNG, JPG, JPEG, GIF, WEBP, SVG
                            </li>
                          </ul>
                        </>
                      }
                      placement="right"
                    >
                      <CIcon
                        icon={cilInfo}
                        className="mx-2 text-danger"
                      ></CIcon>
                    </CTooltip>
                    <input
                      id="image"
                      name="image"
                      type="file"
                      ref={fileInputRef}
                      className={`form-control ${
                        touched.image && errors.image ? "is-invalid" : ""
                      }`}
                      accept="image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml"
                      onChange={(event) => {
                        const image = event.currentTarget.files[0];
                        setFieldValue("image", image);
                      }}
                    />
                    <ErrorMessage
                      name="image"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className=" d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn me-3 ansa-button-secondary"
                    onClick={() => navigate("/banners")}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn ansa-button-primary"
                    disabled={isSubmitting}
                  >
                    Create Banner
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateBanners;
